import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiRequest } from '@/lib/supabase'
import { PlanGate } from '@/components/PlanGate'
import { ConnectInstagram } from '@/components/instagram/ConnectInstagram'
import { RulesList } from '@/components/instagram/RulesList'
import { ExecutionLogs } from '@/components/instagram/ExecutionLogs'

type Tab = 'connection' | 'rules' | 'logs'

interface Rule {
  id: string
  name: string
  trigger_type: string
  keyword: string
  is_active: boolean
  trigger_count: number
  connection: { ig_username: string } | null
  created_at: string
}

export function Instagram() {
  const [activeTab, setActiveTab] = useState<Tab>('connection')

  // Handle OAuth popup callback: notify opener and close, or just clean up URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('connected') !== 'true') return

    if (window.opener) {
      window.opener.postMessage({ type: 'instagram-connected' }, window.location.origin)
      window.close()
    } else {
      // Direct navigation (non-popup flow) — just clean up the URL
      window.history.replaceState({}, '', '/instagram')
    }
  }, [])

  const { data: statusData } = useQuery<{ success: boolean; data: { configured: boolean } }>({
    queryKey: ['ig-oauth-status'],
    queryFn: () => apiRequest('/api/instagram/auth/status'),
  })

  const isConfigured = statusData?.data?.configured ?? true

  const { data: rulesData, isLoading: rulesLoading, error: rulesError } = useQuery<{ success: boolean; data: Rule[] }>({
    queryKey: ['ig-rules'],
    queryFn: () => apiRequest('/api/instagram/rules'),
    retry: false,
    enabled: isConfigured,
  })

  const hasAccess = !(rulesError && (rulesError as Error).message?.includes('not available on your plan'))

  const rules = rulesData?.data || []

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'connection', label: 'Connection', icon: 'link' },
    { key: 'rules', label: 'Rules', icon: 'rule' },
    { key: 'logs', label: 'Logs', icon: 'history' },
  ]

  return (
    <div>
      <div className="flex flex-wrap justify-between items-end gap-3 mb-8">
        <div>
          <h2 className="text-[#0d121b] dark:text-white text-3xl font-black tracking-tight">Instagram Automation</h2>
          <p className="text-[#4c669a] dark:text-gray-400 text-sm mt-1">
            Auto-send DMs when users comment on your posts
          </p>
        </div>
      </div>

      <PlanGate feature="Instagram Automation" requiredPlan="Pro" hasAccess={hasAccess}>
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 dark:bg-[#0d121b] rounded-lg p-1 max-w-md">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md transition-all ${
                activeTab === tab.key
                  ? 'bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
              }`}
            >
              <span className="material-symbols-outlined text-[16px]">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-white dark:bg-[#1c2230] rounded-xl border border-[#e7ebf3] dark:border-white/10 p-5">
          {activeTab === 'connection' && <ConnectInstagram isConfigured={isConfigured} />}
          {activeTab === 'rules' && <RulesList rules={rules} isLoading={rulesLoading} isConfigured={isConfigured} />}
          {activeTab === 'logs' && <ExecutionLogs isConfigured={isConfigured} />}
        </div>
      </PlanGate>
    </div>
  )
}
